import { useEffect, useState } from "react";
import { getAdminProfile } from "../../features/adminAuth/adminAuthStorage";

const formatDateTime = (value) => {
  if (!value) return "Never";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "Never" : parsed.toLocaleString();
};

const InventoryPage = () => {
  const [activeTab, setActiveTab] = useState("providers");
  const [providerForm, setProviderForm] = useState({
    name: "",
    alias: "",
    markupPercentage: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");
  const [providers, setProviders] = useState([]);
  const [isLoadingProviders, setIsLoadingProviders] = useState(false);
  const [providersError, setProvidersError] = useState("");
  const [actionLoadingAlias, setActionLoadingAlias] = useState("");
  const [editingAlias, setEditingAlias] = useState("");
  const [editForm, setEditForm] = useState({ name: "", alias: "", markupPercentage: "" });
  const [currencyForm, setCurrencyForm] = useState({ ghanaRate: "", nigeriaRate: "" });
  const [currencyLoading, setCurrencyLoading] = useState(false);
  const [currencySaving, setCurrencySaving] = useState(false);
  const [currencyError, setCurrencyError] = useState("");
  const [currencySuccess, setCurrencySuccess] = useState("");
  const [currencyUpdatedAt, setCurrencyUpdatedAt] = useState("");
  const [currencyUpdatedBy, setCurrencyUpdatedBy] = useState("");
  const profile = getAdminProfile();

  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || "https://api.verixxon.com";

  const handleProviderChange = (event) => {
    const { name, value } = event.target;
    setProviderForm((prev) => ({ ...prev, [name]: value }));
  };

  const loadProviders = async () => {
    setIsLoadingProviders(true);
    setProvidersError("");

    try {
      const response = await fetch(`${apiBaseUrl}/api/admin/providers`);
      const payload = await response.json();

      if (!response.ok) {
        setProvidersError(payload.message || "Failed to load providers.");
        return;
      }

      setProviders(payload.data || []);
    } catch (error) {
      setProvidersError("Unable to load providers from backend.");
    } finally {
      setIsLoadingProviders(false);
    }
  };

  useEffect(() => {
    if (activeTab === "providers") {
      loadProviders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    const loadCurrencyRates = async () => {
      if (activeTab !== "currency") {
        return;
      }

      setCurrencyLoading(true);
      setCurrencyError("");
      setCurrencySuccess("");

      try {
        const response = await fetch(`${apiBaseUrl}/api/admin/providers/currency-rates`);
        const payload = await response.json();

        if (!response.ok) {
          throw new Error(payload.message || "Failed to load currency rates.");
        }

        const nextGhana = payload?.data?.ghanaRate;
        const nextNigeria = payload?.data?.nigeriaRate;

        setCurrencyForm({
          ghanaRate: Number.isFinite(Number(nextGhana)) && Number(nextGhana) > 0 ? String(nextGhana) : "",
          nigeriaRate: Number.isFinite(Number(nextNigeria)) && Number(nextNigeria) > 0 ? String(nextNigeria) : "",
        });
        setCurrencyUpdatedAt(payload?.data?.updatedAt || "");
        setCurrencyUpdatedBy(payload?.data?.updatedByEmail || "");
      } catch (error) {
        setCurrencyError(error.message || "Unable to load currency rates.");
      } finally {
        setCurrencyLoading(false);
      }
    };

    loadCurrencyRates();
  }, [activeTab, apiBaseUrl]);

  const handleProviderSubmit = async (event) => {
    event.preventDefault();
    setSubmitError("");
    setSubmitSuccess("");

    const trimmedName = providerForm.name.trim();
    const normalizedAlias = providerForm.alias.trim().toLowerCase();
    const parsedMarkup = Number(providerForm.markupPercentage);

    if (!trimmedName || !normalizedAlias || providerForm.markupPercentage === "") {
      setSubmitError("Provider name, alias, and markup percentage are required.");
      return;
    }

    if (Number.isNaN(parsedMarkup) || parsedMarkup < 0) {
      setSubmitError("Markup percentage must be a valid number greater than or equal to 0.");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${apiBaseUrl}/api/admin/providers`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: trimmedName,
          alias: normalizedAlias,
          markupPercentage: parsedMarkup,
        }),
      });

      const payload = await response.json();

      if (!response.ok) {
        setSubmitError(payload.message || "Failed to onboard provider.");
        return;
      }

      setSubmitSuccess(`Saved successfully to ${payload.collectionPath}`);
      setProviderForm({ name: "", alias: "", markupPercentage: "" });
      await loadProviders();
    } catch (error) {
      setSubmitError("Unable to connect to backend service.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const startEditProvider = (provider) => {
    setEditingAlias(provider.alias);
    setEditForm({
      name: provider.name,
      alias: provider.alias,
      markupPercentage: String(provider.markupPercentage),
    });
    setSubmitError("");
    setSubmitSuccess("");
  };

  const cancelEditProvider = () => {
    setEditingAlias("");
    setEditForm({ name: "", alias: "", markupPercentage: "" });
  };

  const handleEditChange = (event) => {
    const { name, value } = event.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const saveProviderEdits = async (targetAlias) => {
    const trimmedName = editForm.name.trim();
    const normalizedAlias = editForm.alias.trim().toLowerCase();
    const parsedMarkup = Number(editForm.markupPercentage);

    if (!trimmedName || !normalizedAlias || editForm.markupPercentage === "") {
      setSubmitError("Edit fields require name, alias, and markup percentage.");
      return;
    }

    if (Number.isNaN(parsedMarkup) || parsedMarkup < 0) {
      setSubmitError("Markup percentage must be a valid number greater than or equal to 0.");
      return;
    }

    setActionLoadingAlias(targetAlias);
    setSubmitError("");
    setSubmitSuccess("");

    try {
      const response = await fetch(`${apiBaseUrl}/api/admin/providers/${targetAlias}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: trimmedName,
          alias: normalizedAlias,
          markupPercentage: parsedMarkup,
        }),
      });

      const payload = await response.json();
      if (!response.ok) {
        setSubmitError(payload.message || "Failed to update provider.");
        return;
      }

      setSubmitSuccess(`Provider updated at ${payload.data.collectionPath}`);
      cancelEditProvider();
      await loadProviders();
    } catch (error) {
      setSubmitError("Unable to connect to backend service.");
    } finally {
      setActionLoadingAlias("");
    }
  };

  const deleteProvider = async (alias) => {
    const shouldDelete = window.confirm(`Delete provider ${alias}?`);
    if (!shouldDelete) {
      return;
    }

    setActionLoadingAlias(alias);
    setSubmitError("");
    setSubmitSuccess("");

    try {
      const response = await fetch(`${apiBaseUrl}/api/admin/providers/${alias}`, {
        method: "DELETE",
      });
      const payload = await response.json();

      if (!response.ok) {
        setSubmitError(payload.message || "Failed to delete provider.");
        return;
      }

      setSubmitSuccess(`Provider removed from ${payload.data.collectionPath}`);
      if (editingAlias === alias) {
        cancelEditProvider();
      }
      await loadProviders();
    } catch (error) {
      setSubmitError("Unable to connect to backend service.");
    } finally {
      setActionLoadingAlias("");
    }
  };

  const toggleProviderSuspension = async (provider) => {
    const endpoint = provider.status === "suspended" ? "unsuspend" : "suspend";
    setActionLoadingAlias(provider.alias);
    setSubmitError("");
    setSubmitSuccess("");

    try {
      const response = await fetch(`${apiBaseUrl}/api/admin/providers/${provider.alias}/${endpoint}`, {
        method: "PATCH",
      });
      const payload = await response.json();

      if (!response.ok) {
        setSubmitError(payload.message || "Failed to update provider status.");
        return;
      }

      setSubmitSuccess(payload.message);
      await loadProviders();
    } catch (error) {
      setSubmitError("Unable to connect to backend service.");
    } finally {
      setActionLoadingAlias("");
    }
  };

  const handleCurrencyChange = (event) => {
    const { name, value } = event.target;
    setCurrencyForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCurrencySave = async (event) => {
    event.preventDefault();
    setCurrencyError("");
    setCurrencySuccess("");

    const parsedGhanaRate = Number(currencyForm.ghanaRate);
    const parsedNigeriaRate = Number(currencyForm.nigeriaRate);

    if (!Number.isFinite(parsedGhanaRate) || parsedGhanaRate <= 0) {
      setCurrencyError("Ghana rate must be a number greater than 0.");
      return;
    }

    if (!Number.isFinite(parsedNigeriaRate) || parsedNigeriaRate <= 0) {
      setCurrencyError("Nigeria rate must be a number greater than 0.");
      return;
    }

    setCurrencySaving(true);

    try {
      const response = await fetch(`${apiBaseUrl}/api/admin/providers/currency-rates`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ghanaRate: parsedGhanaRate,
          nigeriaRate: parsedNigeriaRate,
          updatedByEmail: profile?.email || "",
        }),
      });

      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.message || "Failed to save currency rates.");
      }

      setCurrencySuccess("Currency rates saved successfully.");
      setCurrencyUpdatedAt(payload?.data?.updatedAt || "");
      setCurrencyUpdatedBy(payload?.data?.updatedByEmail || "");
    } catch (error) {
      setCurrencyError(error.message || "Unable to connect to backend service.");
    } finally {
      setCurrencySaving(false);
    }
  };

  return (
    <section className="admin-page-card">
      <h2>Inventory</h2>
      <p>Track available numbers and provider stock levels here.</p>

      <div className="inventory-tabs" role="tablist" aria-label="Inventory sections">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "providers"}
          className={
            activeTab === "providers"
              ? "inventory-tab inventory-tab-active"
              : "inventory-tab"
          }
          onClick={() => setActiveTab("providers")}
        >
          Providers
        </button>

        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "currency"}
          className={
            activeTab === "currency"
              ? "inventory-tab inventory-tab-active"
              : "inventory-tab"
          }
          onClick={() => setActiveTab("currency")}
        >
          Currency Rate
        </button>
      </div>

      {activeTab === "providers" ? (
        <section className="inventory-panel" role="tabpanel" aria-label="Providers">
          <h3>Providers</h3>
          <p>Onboard providers with pricing markup and store each by alias.</p>

          <form className="provider-form" onSubmit={handleProviderSubmit}>
            <div className="provider-form-grid">
              <div>
                <label htmlFor="providerName">Provider Name</label>
                <input
                  id="providerName"
                  name="name"
                  type="text"
                  placeholder="VerifySMS"
                  value={providerForm.name}
                  onChange={handleProviderChange}
                />
              </div>

              <div>
                <label htmlFor="providerAlias">Alias</label>
                <input
                  id="providerAlias"
                  name="alias"
                  type="text"
                  placeholder="verifysms"
                  value={providerForm.alias}
                  onChange={handleProviderChange}
                />
              </div>

              <div>
                <label htmlFor="providerMarkup">Markup Percentage</label>
                <input
                  id="providerMarkup"
                  name="markupPercentage"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="12.5"
                  value={providerForm.markupPercentage}
                  onChange={handleProviderChange}
                />
              </div>
            </div>

            {submitError ? <p className="form-feedback form-feedback-error">{submitError}</p> : null}
            {submitSuccess ? <p className="form-feedback form-feedback-success">{submitSuccess}</p> : null}

            <div className="provider-form-actions">
              <button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Saving..." : "Onboard Provider"}
              </button>
            </div>
          </form>

          <section className="provider-list-block">
            <div className="provider-list-header">
              <h3>Onboarded Providers</h3>
              <button type="button" onClick={loadProviders} disabled={isLoadingProviders}>
                {isLoadingProviders ? "Refreshing..." : "Refresh"}
              </button>
            </div>

            {providersError ? <p className="form-feedback form-feedback-error">{providersError}</p> : null}

            <div className="provider-table-wrap">
              <table className="provider-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Alias</th>
                    <th>Markup %</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {providers.length === 0 && !isLoadingProviders ? (
                    <tr>
                      <td colSpan="5" className="provider-empty-row">
                        No providers found.
                      </td>
                    </tr>
                  ) : null}

                  {providers.map((provider) => {
                    const isEditing = editingAlias === provider.alias;
                    const isActionLoading = actionLoadingAlias === provider.alias;

                    return (
                      <tr key={provider._id || provider.alias}>
                        <td>
                          {isEditing ? (
                            <input name="name" value={editForm.name} onChange={handleEditChange} />
                          ) : (
                            provider.name
                          )}
                        </td>
                        <td>
                          {isEditing ? (
                            <input name="alias" value={editForm.alias} onChange={handleEditChange} />
                          ) : (
                            provider.alias
                          )}
                        </td>
                        <td>
                          {isEditing ? (
                            <input
                              name="markupPercentage"
                              type="number"
                              min="0"
                              step="0.01"
                              value={editForm.markupPercentage}
                              onChange={handleEditChange}
                            />
                          ) : (
                            provider.markupPercentage
                          )}
                        </td>
                        <td>
                          <span
                            className={
                              provider.status === "suspended" ? "status-badge status-suspended" : "status-badge status-active"
                            }
                          >
                            {provider.status}
                          </span>
                        </td>
                        <td>
                          <div className="provider-row-actions">
                            {isEditing ? (
                              <>
                                <button
                                  type="button"
                                  className="row-btn row-btn-primary"
                                  onClick={() => saveProviderEdits(provider.alias)}
                                  disabled={isActionLoading}
                                >
                                  Save
                                </button>
                                <button
                                  type="button"
                                  className="row-btn row-btn-muted"
                                  onClick={cancelEditProvider}
                                  disabled={isActionLoading}
                                >
                                  Cancel
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  className="row-btn row-btn-muted"
                                  onClick={() => startEditProvider(provider)}
                                  disabled={isActionLoading}
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  className="row-btn row-btn-warning"
                                  onClick={() => toggleProviderSuspension(provider)}
                                  disabled={isActionLoading}
                                >
                                  {provider.status === "suspended" ? "Unsuspend" : "Suspend"}
                                </button>
                                <button
                                  type="button"
                                  className="row-btn row-btn-danger"
                                  onClick={() => deleteProvider(provider.alias)}
                                  disabled={isActionLoading}
                                >
                                  Delete
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        </section>
      ) : (
        <section className="inventory-panel" role="tabpanel" aria-label="Currency Rate">
          <h3>Currency Rate</h3>
          <p>
            Set how many local currency units equal $1. These rates are saved permanently and remain active until changed.
          </p>

          <form className="provider-form" onSubmit={handleCurrencySave}>
            <div className="provider-form-grid">
              <div>
                <label htmlFor="ghanaRate">Ghana Rate (GHS per $1)</label>
                <input
                  id="ghanaRate"
                  name="ghanaRate"
                  type="number"
                  min="0.000001"
                  step="0.000001"
                  placeholder="11"
                  value={currencyForm.ghanaRate}
                  onChange={handleCurrencyChange}
                  disabled={currencyLoading}
                />
              </div>

              <div>
                <label htmlFor="nigeriaRate">Nigeria Rate (NGN per $1)</label>
                <input
                  id="nigeriaRate"
                  name="nigeriaRate"
                  type="number"
                  min="0.000001"
                  step="0.000001"
                  placeholder="1450"
                  value={currencyForm.nigeriaRate}
                  onChange={handleCurrencyChange}
                  disabled={currencyLoading}
                />
              </div>

              <div>
                <label>Current Setup</label>
                <input
                  type="text"
                  value={
                    currencyForm.ghanaRate && currencyForm.nigeriaRate
                      ? `1 USD = ${currencyForm.ghanaRate} GHS | ${currencyForm.nigeriaRate} NGN`
                      : "Rates not configured yet"
                  }
                  readOnly
                />
              </div>
            </div>

            <p className="form-feedback">
              Last saved: {formatDateTime(currencyUpdatedAt)}
              {currencyUpdatedBy ? ` by ${currencyUpdatedBy}` : ""}
            </p>

            {currencyError ? <p className="form-feedback form-feedback-error">{currencyError}</p> : null}
            {currencySuccess ? <p className="form-feedback form-feedback-success">{currencySuccess}</p> : null}

            <div className="provider-form-actions">
              <button type="submit" disabled={currencySaving || currencyLoading}>
                {currencySaving ? "Saving..." : "Save Currency Rates"}
              </button>
            </div>
          </form>
        </section>
      )}
    </section>
  );
};

export default InventoryPage;
